import "./style.scss";

import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import StyledButton from "components/shared/StyledButton";
import { PortalType } from "contexts/portal.context";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
// import Edit from "assets/img/svg/edit";
// import Basket from "assets/img/svg/basket";
import { useAppSelector } from "store/store";
import { ChannelCalculationMethod, ChannelType, EPortalType } from "store/types";

import type { CalculationMethod } from "..";
import { calculationMethodsI18n, ChannelKind, channelKindsI18n, channelTypesI18n } from "..";
import { ChannelLegacyCalculationBaseFooter } from "../ChannelLegacyCalculationBase";
import type { IChannel } from "../types";

interface IChannelDetails {
    /**
     * data channel for details
     */
    channel: IChannel;
    /**
     * open edit modal
     */
    onEditChannel: () => void;
    /**
     * open delete modal
     */
    onDeleteChannel: () => void;
    /**
     * If user has edit permissions
     */
    hasEditPermission: boolean;
}

const ChannelDetails = ({ channel, onEditChannel, onDeleteChannel, hasEditPermission }: IChannelDetails) => {
    const [t] = useTranslation();
    const portalType = useContext(PortalType);
    const { getCompanySettings } = useCompanySettings();
    const { channelCompensationGroups } = useAppSelector((state) => state.channels);
    const compensationGroup = channelCompensationGroups.find((group) => group.id === channel.compensationGroupId)?.name;
    const usesGroups = getCompanySettings("uses_interchannel_compensation");
    const usesStocks = getCompanySettings("uses_stock");

    return (
        <div className="channel-details-wrapper">
            {channel.nameTranslations && channel.nameTranslations.length > 0 && (
                <div>
                    <div className="channel-translations-label">
                        <TranslateOutlinedIcon sx={{ width: 16, height: 16 }} />
                        <span style={{ fontSize: "14px" }}>{t("Channels.NameTranslations")}</span>
                    </div>
                    <div className="channel-translations-wrapper">
                        {channel.nameTranslations.map((translation) => (
                            <div key={translation.locale}>
                                <span className="channel-translations-value">
                                    {t(`Locales.${translation.locale}`) + " - " + translation.name}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <div className="channel-details-title">
                <span>{t("NewChannel.ChannelDetails")}</span>
            </div>
            <div className="channel-details">
                {/** Left side details */}
                <div className="channel-specifications-wrapper left">
                    <div className="channel-specification">
                        <label>{t("NewChannel.SalesFormat")}</label>
                        <span>{channelKindsI18n()[channel.kind as keyof Record<ChannelKind, string>]}</span>
                    </div>
                    <div className="channel-specification">
                        <label>{t("Channels.CurrentChannelType")}</label>
                        <span>
                            {
                                calculationMethodsI18n()[
                                    channel.calculationMethod as keyof Record<CalculationMethod, string>
                                ]
                            }
                        </span>
                    </div>
                    {portalType !== EPortalType.BOOK && channel.type != null && (
                        <div className="channel-specification">
                            <label>{t("Channels.CalculationBase")}</label>
                            <span>{channelTypesI18n()[channel.type as keyof Record<ChannelType, string>]}</span>
                        </div>
                    )}
                    {usesGroups && compensationGroup && (
                        <div className="channel-specification">
                            <label>{t("NewChannel.CompensationGroup")}</label>
                            <span>{compensationGroup}</span>
                        </div>
                    )}
                    {channel.distributor && (
                        <div className="channel-specification">
                            <label>{t("NewChannel.Distributor")}</label>
                            <span>{channel.distributor}</span>
                        </div>
                    )}
                    {channel.calculationMethod !== ChannelCalculationMethod.PACKAGE && (
                        <div className="channel-specification">
                            <label>{t("NewChannel.Milestones")}</label>
                            <span>{channel.hasMilestones ? t("NewChannel.Yes") : t("NewChannel.No")}</span>
                        </div>
                    )}
                </div>

                {/** Right side details */}
                <div className="channel-specifications-wrapper right">
                    <div className="channel-specification">
                        <label>{t("NewChannel.AddDesc")}</label>
                        <span>{channel.showNotes ? t("NewChannel.Yes") : t("NewChannel.No")}</span>
                    </div>
                    {channel.calculationMethod !== ChannelCalculationMethod.PACKAGE && (
                        <div className="channel-specification">
                            <label>{t("NewChannel.ShowCumulativeSales")}</label>
                            <span>{channel.showCumulativeSales ? t("NewChannel.Yes") : t("NewChannel.No")}</span>
                        </div>
                    )}
                    {usesStocks && channel.kind == ChannelKind.PHYSICAL && (
                        <div className="channel-specification">
                            <label>{t("NewChannel.ShowInStock")}</label>
                            <span>{channel.showInStock ? t("NewChannel.Yes") : t("NewChannel.No")}</span>
                        </div>
                    )}
                </div>
            </div>

            {portalType === EPortalType.BOOK && <ChannelLegacyCalculationBaseFooter channel={channel} />}

            {/** Edit/Delete channel btn */}
            {hasEditPermission ? (
                <div className="channel-edit-btn-container">
                    <StyledButton
                        className="channel-btn edit"
                        buttonType="secondary"
                        id={`edit-btn-${channel.name}`}
                        onClick={() => {
                            onEditChannel();
                        }}
                    >
                        <DriveFileRenameOutlineOutlinedIcon />
                        <span>{t("Channels.EditChannel")}</span>
                    </StyledButton>
                    <StyledButton
                        className="channel-btn delete"
                        buttonType="secondary"
                        id={`delete-btn-${channel.name}`}
                        onClick={() => {
                            onDeleteChannel();
                        }}
                    >
                        <DeleteOutlineOutlinedIcon />
                    </StyledButton>
                </div>
            ) : (
                <br />
            )}
        </div>
    );
};

export default ChannelDetails;
